import { useState, useEffect, useCallback, useRef } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut, adminPostForm } from "lib/adminClient";
import { useResource } from "hooks/useResource";
import { getInstitutions } from "services/content";
import { mediaUrl } from "lib/media";

/**
 * Teaching staff, shown on each institution page.
 *
 * One row per teacher, carrying exactly what the school's own form asks for:
 * name, designation, highest qualification, mobile number, which institution,
 * and a recent photograph.
 *
 * The institution choices come from the institutions blob rather than being
 * typed in, so a slug can never be misspelt into a teacher who appears nowhere.
 *
 * The whole blob is replaced on save, so a failed load has to block saving —
 * otherwise an empty screen would wipe the staff list.
 */

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";
const addBtnCls =
  "bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90";
const delBtnCls = "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]";
const moveBtnCls = "bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs";

/** Uploads live at the API origin; anything else is served by the site. */
const srcFor = (p) => (p?.startsWith("/uploads/") ? mediaUrl(p) : p);

const blank = () => ({
  name: "",
  designation: "",
  qualification: "",
  phone: "",
  institution: "",
  photo: "",
});

export default function TeachersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const fileRefs = useRef({});

  const { data: instData } = useResource(getInstitutions);
  const institutions = (instData?.items || []).filter((i) => i.slug);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/teachers")) || {};
      setItems(d.items || []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upd = (i, field, value) =>
    setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, [field]: value } : x)));

  const move = (i, dir) =>
    setItems((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const copy = [...arr];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const uploadPhoto = async (i, file) => {
    if (!file) return;
    setBusy(`p${i}`);
    setError("");
    try {
      const form = new FormData();
      form.append("image", file);
      const { url } = await adminPostForm("/admin/media", form);
      upd(i, "photo", url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    if (loadFailed) {
      setError("Staff didn't load, so saving now would erase the list. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      // A row with no name, or none attached to an institution, would show up
      // on no page at all — drop it rather than store a ghost.
      const payload = {
        items: items.filter((t) => (t.name || "").trim() && (t.institution || "").trim()),
      };
      const saved = await adminPut("/admin/content/teachers", payload);
      if (saved) setItems(saved.items || []);
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  const countFor = (slug) => items.filter((t) => t.institution === slug).length;
  const incomplete = items.filter(
    (t) => !(t.name || "").trim() || !(t.institution || "").trim()
  ).length;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}
      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Teachers"
          action={
            <button
              type="button"
              className={addBtnCls}
              onClick={() => setItems((arr) => [...arr, blank()])}
            >
              + Add teacher
            </button>
          }
        />
        <div className="p-[22px]">
          <p className="text-muted text-[12.5px] mb-4">
            Each teacher appears on their institution's page, in this order. Name and institution
            are required — a row missing either is dropped when you save. The mobile number is
            shown publicly on the website.
          </p>

          <div className="flex flex-wrap gap-3 mb-5">
            {institutions.map((i) => (
              <span key={i.slug} className="rounded-lg bg-[#f7f8f8] border border-line px-3 py-1.5">
                <span className="text-[12px] text-muted">{i.short || i.slug} </span>
                <strong className="text-[13px] text-ink">{countFor(i.slug)}</strong>
              </span>
            ))}
            {incomplete > 0 && (
              <span className="rounded-lg bg-[#fdf6e7] border border-gold/40 px-3 py-1.5 text-[12.5px] text-gold">
                {incomplete} row{incomplete > 1 ? "s" : ""} missing a name or institution
              </span>
            )}
          </div>

          {items.length === 0 && <p className="text-muted text-sm italic">No teachers yet.</p>}

          {items.map((t, i) => (
            <div
              key={i}
              className="flex gap-3 items-start border-[1.5px] border-line rounded-xl p-4 mb-3"
            >
              {/* Photograph */}
              <div className="shrink-0 text-center">
                <div className="w-[64px] h-[78px] rounded-lg border border-line bg-[#f7f8f8] overflow-hidden flex items-center justify-center">
                  {t.photo ? (
                    <img src={srcFor(t.photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] text-muted">No photo</span>
                  )}
                </div>
                <input
                  ref={(el) => (fileRefs.current[`p${i}`] = el)}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadPhoto(i, e.target.files?.[0])}
                />
                <button
                  type="button"
                  className={`${moveBtnCls} mt-1.5 w-full`}
                  disabled={busy === `p${i}`}
                  onClick={() => fileRefs.current[`p${i}`]?.click()}
                >
                  {busy === `p${i}` ? "…" : t.photo ? "Replace" : "Upload"}
                </button>
              </div>

              {/* The five details */}
              <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">
                    <span className="font-bn">নাম</span> / Name
                  </span>
                  <input
                    className={`${inputCls} font-bn`}
                    value={t.name || ""}
                    onChange={(e) => upd(i, "name", e.target.value)}
                    placeholder="মোছা. শাহানারা বেগম"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">
                    <span className="font-bn">পদবী</span> / Designation
                  </span>
                  <input
                    className={`${inputCls} font-bn`}
                    value={t.designation || ""}
                    onChange={(e) => upd(i, "designation", e.target.value)}
                    placeholder="সহকারী শিক্ষক"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">
                    <span className="font-bn">শিক্ষাগত যোগ্যতা (সর্বশেষ)</span> / Qualification
                  </span>
                  <input
                    className={`${inputCls} font-bn`}
                    value={t.qualification || ""}
                    onChange={(e) => upd(i, "qualification", e.target.value)}
                    placeholder="এম.এ., বি.এড."
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">
                    <span className="font-bn">মোবাইল নং</span> / Mobile
                  </span>
                  <input
                    className={inputCls}
                    value={t.phone || ""}
                    onChange={(e) => upd(i, "phone", e.target.value)}
                    placeholder="01XXXXXXXXX"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">
                    <span className="font-bn">সংশ্লিষ্ট প্রতিষ্ঠান</span> / Institution
                  </span>
                  <select
                    className={`${inputCls} cursor-pointer`}
                    value={t.institution || ""}
                    onChange={(e) => upd(i, "institution", e.target.value)}
                  >
                    <option value="">Choose…</option>
                    {institutions.map((o) => (
                      <option key={o.slug} value={o.slug}>
                        {o.short ? `${o.short} — ${o.name}` : o.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex flex-col gap-1.5 shrink-0">
                <button type="button" className={moveBtnCls} onClick={() => move(i, -1)}>
                  ↑
                </button>
                <button type="button" className={moveBtnCls} onClick={() => move(i, 1)}>
                  ↓
                </button>
                <button
                  type="button"
                  className={delBtnCls}
                  onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-lg px-5 py-2.5 font-sans font-semibold text-sm cursor-pointer disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        {status && <span className="text-[13px] text-green font-semibold">{status}</span>}
      </div>
    </div>
  );
}
